import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Container,
} from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SpaIcon from '@mui/icons-material/Spa';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

function Home() {
  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh',
      py: 4
    }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          sx={{ 
            textAlign: 'center',
            color: '#2c3e50',
            fontWeight: 'bold',
            mb: 4,
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          Welcome to Fitness & Wellness Guide
        </Typography>
        
        <Grid container spacing={4}>
          {/* Main Introduction */}
          <Grid item xs={12}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                mb: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '15px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Your Journey to Better Health Starts Here
              </Typography>
              <Typography paragraph sx={{ fontSize: '1.1rem', opacity: 0.9 }}>
                Discover comprehensive guides for fitness, nutrition, and overall wellness. Whether you're just starting your fitness journey or looking to enhance your current routine, we're here to help you achieve your health goals.
              </Typography>
            </Paper>
          </Grid>

          {/* Fitness Section */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                height: '100%',
                borderRadius: '15px',
                background: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)',
                color: '#2c3e50',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <FitnessCenterIcon sx={{ fontSize: 40, mr: 1, color: '#2c3e50' }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Essential Fitness Tips
                  </Typography>
                </Box>
                <List>
                  <ListItem sx={{ mb: 2 }}>
                    <ListItemText 
                      primary="Start Gradually"
                      secondary="Begin with light exercises and gradually increase intensity to prevent injury."
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                  <ListItem sx={{ mb: 2 }}>
                    <ListItemText 
                      primary="Stay Consistent"
                      secondary="Aim for at least 30 minutes of exercise 3-4 times per week."
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                  <ListItem sx={{ mb: 2 }}>
                    <ListItemText 
                      primary="Mix It Up"
                      secondary="Combine cardio, strength training, and flexibility exercises for balanced fitness."
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Rest and Recovery"
                      secondary="Allow your body time to recover between workouts."
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Diet Section */}
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                height: '100%',
                borderRadius: '15px',
                background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                color: '#2c3e50',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <RestaurantIcon sx={{ fontSize: 40, mr: 1, color: '#2c3e50' }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Healthy Eating Guidelines
                  </Typography>
                </Box>
                <List>
                  <ListItem sx={{ mb: 2 }}>
                    <ListItemText 
                      primary="Balanced Diet"
                      secondary="Include a variety of fruits, vegetables, lean proteins, and whole grains."
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                  <ListItem sx={{ mb: 2 }}>
                    <ListItemText 
                      primary="Portion Control"
                      secondary="Be mindful of portion sizes and avoid overeating."
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                  <ListItem sx={{ mb: 2 }}>
                    <ListItemText 
                      primary="Stay Hydrated"
                      secondary="Drink plenty of water throughout the day."
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Limit Processed Foods"
                      secondary="Choose whole, unprocessed foods when possible."
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Wellness Tips */}
          <Grid item xs={12}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                mt: 2,
                background: 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)',
                borderRadius: '15px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SpaIcon sx={{ fontSize: 40, mr: 1, color: '#2c3e50' }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                  General Wellness Tips
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.9)' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                        Sleep Well
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Aim for 7-9 hours of quality sleep each night to support recovery and overall health.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.9)' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                        Manage Stress
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Practice stress-reduction techniques like meditation, yoga, or deep breathing exercises.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.9)' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                        Regular Check-ups
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Schedule regular health check-ups and stay up to date with preventive care.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Call to Action */}
          <Grid item xs={12}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                mt: 2, 
                background: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
                borderRadius: '15px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmojiEventsIcon sx={{ fontSize: 40, mr: 1, color: '#2c3e50' }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                  Ready to Start Your Journey?
                </Typography>
              </Box>
              <Typography paragraph sx={{ fontSize: '1.1rem', color: '#2c3e50' }}>
                Remember, everyone's fitness journey is unique. Start with small, achievable goals and build from there. If you have any questions or need guidance, feel free to contact us through our Contact page.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Home; 